import { instanceKey } from "@/lib/instanceKey";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import {
  SkyAccordion,
  SkyAccordionItem,
  SkyBadge,
  SkyButton,
  SkyDataGrid,
  SkyInput,
  SkyMessageBar,
  SkyRadioGroup,
  SkyText,
  SkyTooltip,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useMemo,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import { Link, useLocation, useSearch } from "wouter";
import { dbtNoRowsLabel, isDbtFailedStatus } from "@shared/types/dbtCauses";
import {
  CHOSEN_MODELS_ID,
  type TransformationGraph,
  type TransformationGraphNode,
  type DbtJobRunRow,
} from "@shared/types/transformations";
import { LoadingRows } from "@/components/access/ScreenStates";
import { usePermissions } from "@/hooks/use-permissions";
import { DBT_JOBS_GRAPH_PATH, useDbtJobRuns, useDbtJobs } from "@/hooks/use-dbt-jobs";
import { AddIcon, ExternalLinkIcon, PlayIcon, RetryIcon } from "@/lib/icons";
import { pixelColumnLayout } from "@/lib/grid";
import { ROUTES, absolutePath } from "@/lib/routes";
import {
  ASK_ADMIN_ABOUT_DBT_REPOSITORY,
  DBT_SETTINGS_PATH,
  OPEN_DBT_SETTINGS_LABEL,
} from "@/lib/transformations/dbtSettings";
import {
  GROUP_BY_LABELS,
  MODEL_GROUP_BYS,
  INLINED_BY_DBT_SENTENCE,
  SOURCES_GROUP_KEY,
  defaultGroupBy,
  groupModels,
  isInlinedRow,
  isModelGroupBy,
  modelRows,
  rowFailed,
  type ModelGroup,
  type ModelGroupBy,
  type ModelRow,
} from "@/lib/transformations/modelGroups";
import { repoFileUrl, repoHostName } from "@/lib/transformations/repoFileUrl";
import {
  isRunInFlight,
  phasePhrase,
  runOutcome,
  skipReasonWord,
} from "@/lib/transformations/runOutcome";
import { CauseBlock } from "./CauseBlock";
import { formatSeconds, materializationLabel, runStartOf } from "./transformFormatting";
import { formatInstant, instantHoverTitle } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * The Transformations landing's Models view (Session 11, ux-s11-transformations.md §3.1; Studio's
 * DBT tab, shot 1): every model, seed and snapshot of the dbt project, grouped by Schema (the
 * layer), Folder, Tag or None, each saying its last result, when it was last built, its rows and
 * time, and which transformation selects it.
 *
 * Reads the graph the landing already holds (one read per visit plus Refresh; never polled) and
 * nothing else of its own but two cached lists the landing also reads. The grouping is pure
 * (`modelGroups.ts`); the choice is kept in `?group=` and the failure filter in `?result=`, so a
 * link keeps both. Models dbt disables come from `graph.disabledNodes` (only this view reads it)
 * and read "Disabled in dbt", greyed. Viewer-safe: the actions are "Run all dbt jobs now" and
 * (round 6, T3) the model choice with "Run selected (n)", rendered for `runs.start` only.
 *
 * No Query control here (ratification 2): Query is on the History and Tests views.
 */

export type GraphState = "loading" | "ready" | "error";

export interface TransformationModelsViewProps {
  graph: TransformationGraph | undefined;
  graphState: GraphState;
  onRetry(): void;
  canStart: boolean;
  onRunNow(): void;
  onRefresh(): void;
  /**
   * Review nit (UX §3.4 "No transformation yet"): opens the landing's New dbt job drawer.
   * Optional: the button renders only when it is given and the user holds
   * `dbt_jobs.write`.
   */
  onNewDbtJob?: () => void;
  /**
   * Round 6 (T3): "Run selected (n)" hands the chosen models' dbt names, in name order, to the
   * landing, which opens the Run dialog for them. Optional: rows can be chosen only when it is
   * given and the user holds `runs.start` (`canStart`). Round 6 T-29: `every` is true when the
   * choice is every model that can be chosen, so the landing runs every dbt job instead of sending
   * a list that could only grow past the selection limit.
   */
  onRunSelected?: (models: string[], every: boolean) => void;
}

/* ------------------------------------------------------------------------------------- *
 * The address the views own (`?group=`, `?result=`); the landing owns `?view=`
 * ------------------------------------------------------------------------------------- */

/** One search parameter of the Transformations landing, read and replaced in place. */
export function useLandingParam(
  name: string,
): [string | null, (value: string | null, extra?: Record<string, string | null>) => void] {
  const search = useSearch();
  const [, navigate] = useLocation();
  const value = new URLSearchParams(search).get(name);
  const set = useCallback(
    (next: string | null, extra: Record<string, string | null> = {}) => {
      const params = new URLSearchParams(search);
      for (const [key, entry] of Object.entries({ [name]: next, ...extra })) {
        if (entry === null) params.delete(key);
        else params.set(key, entry);
      }
      const query = params.toString();
      const queryPart = query ? `?${query}` : "";
      navigate(absolutePath(`${ROUTES.transformations}${queryPart}`), {
        replace: true,
      });
    },
    [name, navigate, search],
  );
  return [value, set];
}

/* ------------------------------------------------------------------------------------- *
 * The states every project view shares (UX §3.4, contract §6.7 rulings)
 * ------------------------------------------------------------------------------------- */

export function GraphLoading({ testId }: { testId: string }) {
  return (
    <div data-testid={testId}>
      <LoadingRows rows={6} columns={6} />
    </div>
  );
}

export function GraphReadError({ onRetry, testId }: { onRetry(): void; testId: string }) {
  return (
    <SkyMessageBar
      intent="error"
      layout="multiline"
      title="We could not read the project."
      message="The request for the dbt project's facts did not answer. Try again."
      testId={testId}
      actions={
        <SkyButton appearance="secondary" size="small" icon={RetryIcon} onClick={onRetry}>
          Retry
        </SkyButton>
      }
    />
  );
}

function OpenDestination() {
  const [, navigate] = useLocation();
  return (
    <SkyButton
      appearance="secondary"
      size="small"
      testId="transformation-view-open-destination"
      onClick={() => navigate(absolutePath(DBT_SETTINGS_PATH))}
    >
      {OPEN_DBT_SETTINGS_LABEL}
    </SkyButton>
  );
}

type Bar = (message: ReactNode, actions?: ReactNode, title?: string) => ReactNode;

interface ReadBaseline {
  graph: TransformationGraph;
  readAt: number | null;
  runId: string | null;
  inFlight: boolean;
}

/**
 * Review r1 SF1: the baseline belongs to one read of the list, not to one graph object (an
 * unchanged re-read keeps the object), and it is taken from a runs page read after the list, so a
 * runs page cached from before cannot set it. Set during render; returns the baseline only while
 * it is the current read's.
 */
function useReadBaseline(
  graph: TransformationGraph,
  graphReadAt: number | null,
  lastRuns: ReturnType<typeof useDbtJobRuns>,
  newest: DbtJobRunRow | null,
): [ReadBaseline | null, (baseline: ReadBaseline) => void] {
  const [readAgainst, setReadAgainst] = useState<ReadBaseline | null>(null);
  const baselineCurrent =
    readAgainst !== null && readAgainst.graph === graph && readAgainst.readAt === graphReadAt;
  if (!baselineCurrent && runsReadAfterList(lastRuns, graphReadAt)) {
    setReadAgainst({
      graph,
      readAt: graphReadAt,
      runId: newest?.runId ?? null,
      inFlight: newest ? isRunInFlight(newest) : false,
    });
  }
  return [baselineCurrent ? readAgainst : null, setReadAgainst];
}

/** The runs page was read after the project list (or there is no list read time to compare). */
function runsReadAfterList(
  lastRuns: ReturnType<typeof useDbtJobRuns>,
  graphReadAt: number | null,
): boolean {
  if (!lastRuns.data || lastRuns.isFetching) return false;
  return graphReadAt === null || lastRuns.dataUpdatedAt >= graphReadAt;
}

/**
 * The newest run finished after the list was read: seen in flight or appearing since the read
 * (`baseline`), or finished after the graph's own read time.
 */
function endedAfterRead(
  newest: DbtJobRunRow | null,
  baseline: { runId: string | null; inFlight: boolean } | null,
  graphReadAt: number | null,
): boolean {
  if (newest === null || isRunInFlight(newest) || newest.stepStatus === "skipped") return false;
  if (baseline && (baseline.runId !== newest.runId || baseline.inFlight)) return true;
  return (
    graphReadAt !== null &&
    newest.finishedAt !== null &&
    Date.parse(newest.finishedAt) > graphReadAt
  );
}

/** No run has listed the project yet: what to do first, by what is known about the runs. */
function noRunYetBar({
  bar,
  noun,
  testId,
  newest,
  noDbtJobs,
  onNewDbtJob,
  finishedAfterRead,
  onRefresh,
  onRunNow,
}: {
  bar: Bar;
  noun: "models" | "tests";
  testId: string;
  newest: DbtJobRunRow | null;
  noDbtJobs: boolean;
  /** New dbt job, for a reader who may write one. */
  onNewDbtJob: (() => void) | undefined;
  finishedAfterRead: boolean;
  onRefresh: () => void;
  /** Run all dbt jobs now, for a reader with `runs.start`. */
  onRunNow: (() => void) | undefined;
}): ReactNode {
  if (noDbtJobs) {
    return bar(
      `Create a dbt job to choose which models run, then run it once to list the project's ${noun}.`,
      onNewDbtJob ? (
        <SkyButton
          appearance="secondary"
          size="small"
          icon={AddIcon}
          onClick={onNewDbtJob}
          testId={`${testId}-new-transformation`}
        >
          New dbt job
        </SkyButton>
      ) : undefined,
    );
  }
  if (newest && isRunInFlight(newest)) {
    return bar(
      `The first run is in progress. The ${noun} appear when it reaches dbt. Refresh when it finishes.`,
    );
  }
  if (finishedAfterRead) {
    return bar(
      "The last run finished after this list was read.",
      <SkyButton
        appearance="secondary"
        size="small"
        icon={RetryIcon}
        onClick={onRefresh}
        testId={`${testId}-refresh`}
      >
        Refresh
      </SkyButton>,
    );
  }
  if (newest && newest.stepStatus === "skipped") {
    return bar(
      `No run has reached dbt yet: the last run was skipped (${skipReasonWord(newest.skippedReason)}). Run all dbt jobs once to list the ${noun}.`,
    );
  }
  if (noun === "tests") {
    return bar("Run all dbt jobs once, with Run tests on, to list the tests.");
  }
  // D7 (the ruling prescribes the wording): both sentences for every role, the button for
  // `runs.start` only.
  return bar(
    <span className="flex flex-col gap-1">
      <span>Run all dbt jobs once to list the models.</span>
      <span>Listing models before a run needs a project scan, which is not available yet.</span>
    </span>,
    onRunNow ? (
      <SkyButton
        appearance="secondary"
        size="small"
        icon={PlayIcon}
        onClick={onRunNow}
        testId="transformation-models-run-now"
      >
        Run all dbt jobs now
      </SkyButton>
    ) : undefined,
  );
}

/** A run happened but its project list is not readable: storage, or the run stopped first. */
function projectUnreadBar({
  graph,
  run,
  bar,
  noun,
  testId,
  onRetry,
}: {
  graph: TransformationGraph;
  run: NonNullable<TransformationGraph["run"]>;
  bar: Bar;
  noun: "models" | "tests";
  testId: string;
  onRetry(): void;
}): ReactNode {
  if (graph.artifacts.storageError) {
    return (
      <SkyMessageBar
        intent="warning"
        layout="multiline"
        message="The run's results could not be read from storage."
        testId={testId}
        actions={
          <SkyButton appearance="secondary" size="small" icon={RetryIcon} onClick={onRetry}>
            Retry
          </SkyButton>
        }
      />
    );
  }
  const outcome = runOutcome(run);
  const viewRun = (
    <Link href={absolutePath(`/runs/${run.runId}`)} className="underline-offset-2 hover:underline">
      <SkyText size={200} color="accent">
        View run
      </SkyText>
    </Link>
  );
  return bar(
    outcome.kind === "stopped"
      ? `The last run stopped while ${phasePhrase(outcome.phase ?? "")} before dbt read the project. The ${noun} appear after the next run that reaches dbt.`
      : `The last run's list of ${noun} could not be read. The ${noun} appear after the next run that reaches dbt.`,
    viewRun,
  );
}

/**
 * Why the project list is empty, when it is: said in words with the one action that helps. The
 * screen never shows an empty grid as if the project had no models. `noun` is "models" or
 * "tests"; null means the list can be shown.
 */
export function ProjectEmptyState({
  graph,
  noun,
  canStart,
  onRunNow,
  onRetry,
  onNewDbtJob,
  testId,
}: {
  graph: TransformationGraph;
  noun: "models" | "tests";
  canStart: boolean;
  onRunNow?: () => void;
  onRetry(): void;
  onNewDbtJob?: () => void;
  testId: string;
}): ReactNode {
  const { canAny } = usePermissions();
  const transformations = useDbtJobs(true);
  const lastRuns = useDbtJobRuns(1, graph.run === null);
  const newest: DbtJobRunRow | null = lastRuns.data?.rows[0] ?? null;

  // Ruling 18 (F2a): the list is read once per visit and on Refresh, while the landing polls the
  // runs, so a run that ends after the read is not in it. Two ways to know a run ended after the
  // read: (1) this screen saw it in flight, or saw it appear, since the list was read (or since
  // Refresh); (2) it finished after the landing's graph read, for a screen opened after that (the
  // graph's own read time, and only when the cached graph is the one shown). A run that ended
  // before the read never reached dbt, and the sentences below stand. Adjusted during render,
  // not in an effect.
  // Subscribed so that a graph read ending re-renders this, and the read time below is current.
  const graphKey = instanceKey(DBT_JOBS_GRAPH_PATH, useTenantInstance().selectedInstance?.id);
  useIsFetching({ queryKey: graphKey, exact: true });
  const graphQuery = useQueryClient().getQueryState<TransformationGraph>(graphKey);
  const graphReadAt = graphQuery?.data === graph ? graphQuery.dataUpdatedAt : null;
  const [baseline, setReadAgainst] = useReadBaseline(graph, graphReadAt, lastRuns, newest);
  const finishedAfterRead = endedAfterRead(newest, baseline, graphReadAt);
  const refresh = () => {
    // Refresh reads the list again: the newest run is from before that read.
    setReadAgainst({ graph, readAt: graphReadAt, runId: newest?.runId ?? null, inFlight: false });
    onRetry();
  };

  const bar = (message: ReactNode, actions?: ReactNode, title?: string) => (
    <SkyMessageBar
      intent="info"
      layout="multiline"
      title={title}
      message={message}
      actions={actions}
      testId={testId}
    />
  );

  if (!graph.bundle.repoBound) {
    return bar(
      `Connect a dbt repository to list its ${noun}.`,
      canAny("instance.binding.read") ? <OpenDestination /> : undefined,
    );
  }
  if (graph.run === null) {
    return noRunYetBar({
      bar,
      noun,
      testId,
      newest,
      noDbtJobs: Boolean(transformations.data && transformations.data.length === 0),
      onNewDbtJob: onNewDbtJob && canAny("dbt_jobs.write") ? onNewDbtJob : undefined,
      finishedAfterRead,
      onRefresh: refresh,
      onRunNow: canStart ? onRunNow : undefined,
    });
  }
  if (graph.project === null)
    return projectUnreadBar({ graph, run: graph.run, bar, noun, testId, onRetry });
  return null;
}

/** A run id's own UTC timestamp (`xxxxxxxx-YYYYMMDDHHMMSS-xxxxxxxx`), for a run the view has no row of. */
export function runIdTime(runId: string): string | null {
  const match = /^[0-9a-f]{8}-(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})-[0-9a-f]{8}$/.exec(runId);
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match;
  const at = new Date(`${y}-${mo}-${d}T${h}:${mi}:${s}Z`);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/** "Project from the run of {time} ({run link})": the run whose manifest the list is from. */
export function ProjectFrom({ graph, testId }: { graph: TransformationGraph; testId: string }) {
  const zone = useDisplayTz();
  const run = graph.run;
  const fromRunId = graph.project?.fromRunId ?? run?.runId ?? null;
  if (!fromRunId) return null;
  // #1214: the run the Runs list shows, by its own start and its outcome. A run walked back to
  // (the list is from an earlier run) is known by its id alone, so only its creation minute.
  const known = run && fromRunId === run.runId ? run : null;
  const at = known ? runStartOf(known) : runIdTime(fromRunId);
  return (
    <span data-testid={testId}>
      {"Project from the run of "}
      {at
        ? formatInstant(at, zone, known ? "seconds" : "short", { named: true })
        : "an earlier run"}
      {" ("}
      <Link
        href={absolutePath(`/runs/${fromRunId}`)}
        className="underline-offset-2 hover:underline"
      >
        <span className="font-mono">{fromRunId}</span>
      </Link>
      {")"}
      {known ? `, ${runOutcome(known).word}` : null}
    </span>
  );
}

/** "The last run stopped while {phase in words}. This list is from an earlier run." when walked back. */
export function WalkedBackNote({ graph, testId }: { graph: TransformationGraph; testId: string }) {
  const run = graph.run;
  const from = graph.project?.fromRunId;
  if (!run || !from || from === run.runId) return null;
  const outcome = runOutcome(run);
  return (
    <SkyMessageBar
      intent="info"
      layout="multiline"
      testId={testId}
      message={
        outcome.kind === "stopped"
          ? `The last run stopped while ${phasePhrase(outcome.phase ?? "")}. This list is from an earlier run.`
          : "The last run did not record the project. This list is from an earlier run."
      }
    />
  );
}

/* ------------------------------------------------------------------------------------- *
 * Cells
 * ------------------------------------------------------------------------------------- */

const KIND_BADGES: Record<string, string> = { seed: "Seed", snapshot: "Snapshot", model: "Model" };

/** dbt's status of a node's last build, in the view's words. */
export function lastResultWord(status: string): {
  word: string;
  color: "success" | "danger" | "warning" | "subtle";
} {
  if (status === "success") return { word: "Succeeded", color: "success" };
  if (isDbtFailedStatus(status)) return { word: "Failed", color: "danger" };
  if (status === "skipped") return { word: "Skipped", color: "warning" };
  if (status === "no-op") return { word: "No change", color: "subtle" };
  return { word: "Status not recognised", color: "subtle" };
}

function RunLink({ runId, children }: { runId: string; children: ReactNode }) {
  return (
    <Link
      href={absolutePath(`/runs/${runId}`)}
      className="underline-offset-2 hover:underline"
      aria-label={`Open run ${runId}`}
    >
      <SkyText size={200} color="accent">
        {children}
      </SkyText>
    </Link>
  );
}

export const ROWS_HINT = "Row counts are reported for tables on Snowflake. Views have none.";

export function Dash({ label, hint }: { label: string; hint?: string }) {
  const dash = (
    <SkyText size={200} color="muted" aria-label={label}>
      —
    </SkyText>
  );
  return hint ? (
    <SkyTooltip content={hint}>
      <span tabIndex={0} aria-label={label}>
        <SkyText size={200} color="muted">
          —
        </SkyText>
      </span>
    </SkyTooltip>
  ) : (
    dash
  );
}

interface CellContext {
  graph: TransformationGraph;
  selectionNames: Map<string, string>;
  firstSelector: (node: TransformationGraphNode) => string | null;
  /** The graph's selection is a run of chosen models (round 6, T3), not saved transformations. */
  chosenRun: boolean;
  /** Round 7: the server evaluated the saved dbt jobs (`graph.savedSelection`); the column reads them. */
  saved: boolean;
}

/**
 * Round 7: the dbt jobs the "dbt job" column names for a node. With `graph.savedSelection`, the
 * enabled saved jobs that select it whatever ran last (`savedBy`); from an older server, the
 * last run's own selection (`selectedBy`), as before.
 */
function shownJobs(
  node: TransformationGraphNode,
  saved: boolean,
): { ids: string[]; known: boolean } {
  if (saved) return { ids: node.savedBy ?? [], known: node.savedByKnown ?? false };
  return { ids: node.selectedBy, known: node.selectedByKnown };
}

/** A model's name: muted when disabled, the link colour when it opens a dbt job's graph. */
function modelNameColor(disabled: boolean, linked: boolean): "muted" | "accent" | "default" {
  if (disabled) return "muted";
  return linked ? "accent" : "default";
}

function ModelNameCell({ row, context }: { row: ModelRow; context: CellContext }) {
  const kind = row.node.resourceType;
  const disabled = row.kind === "disabled";
  const dbtJobId = row.kind === "node" ? context.firstSelector(row.node) : null;
  // The app's own font (round 4): a model's name is a name, not code; SQL and selectors keep
  // monospace.
  const name = (
    <SkyText
      size={200}
      weight="semibold"
      color={modelNameColor(disabled, Boolean(dbtJobId))}
      truncate
      block
      title={row.uniqueId}
    >
      {row.name}
    </SkyText>
  );
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      {dbtJobId ? (
        <Link
          href={absolutePath(`${ROUTES.transformations}/${dbtJobId}`)}
          className="min-w-0 underline-offset-2 hover:underline"
          aria-label={`${row.name}: open the graph of the dbt job that selects it`}
        >
          {name}
        </Link>
      ) : (
        name
      )}
      {kind === "model" || kind === "seed" || kind === "snapshot" ? (
        <SkyBadge
          appearance="outline"
          color="subtle"
          testId={`transformation-model-kind-${row.name}`}
        >
          {KIND_BADGES[kind]}
        </SkyBadge>
      ) : null}
    </div>
  );
}

function LastResultCell({ row }: { row: ModelRow }) {
  if (row.kind === "disabled") {
    return (
      <SkyText size={200} color="muted" testId={`transformation-model-result-${row.name}`}>
        Disabled in dbt
      </SkyText>
    );
  }
  const last = row.node.lastBuilt ?? null;
  if (!last) {
    return (
      <SkyText size={200} color="muted" testId={`transformation-model-result-${row.name}`}>
        {isInlinedRow(row) ? INLINED_BY_DBT_SENTENCE : "Not built yet"}
      </SkyText>
    );
  }
  const { word, color } = lastResultWord(last.status);
  const seconds = last.executionTimeSeconds;
  return (
    // `items-start`: the grid stretches a cell's direct child, so the badge drew as a bar across
    // the column (StatusCell's rule, client/AGENTS.md). Round 6 (W3): `min-w-0`, so a long
    // relation in the cause wraps inside this column instead of running over Last built.
    <div
      className="flex min-w-0 flex-col items-start gap-1"
      data-testid={`transformation-model-result-${row.name}`}
    >
      <SkyBadge appearance="tint" color={color}>
        {word}
      </SkyBadge>
      {/* Round 6 (gridsfit): the build's time, under its result (the Runs list's "took"). */}
      {typeof seconds === "number" && seconds >= 0 ? (
        <SkyText
          size={200}
          color="muted"
          font="numeric"
          testId={`transformation-model-time-${row.name}`}
        >
          {`took ${formatSeconds(seconds)}`}
        </SkyText>
      ) : null}
      {last.cause && isDbtFailedStatus(last.status) ? (
        <CauseBlock
          cause={last.cause}
          compact
          whyOnlyWhenGeneric
          testId={`transformation-model-cause-${row.name}`}
        />
      ) : null}
    </div>
  );
}

/**
 * The last success: a link to its run when the last build is that success, its time alone
 * otherwise, or that the model never succeeded.
 */
function lastSucceeded(
  last: TransformationGraphNode["lastBuilt"],
  succeededAt: string | null,
  zone: string,
): ReactElement {
  if (!succeededAt) {
    return (
      <SkyText size={200} color="muted">
        Never succeeded
      </SkyText>
    );
  }
  if (last && last.status === "success" && last.at === succeededAt) {
    return (
      <RunLink runId={last.runId}>
        <span title={instantHoverTitle(succeededAt, zone)}>
          {formatInstant(succeededAt, zone, "seconds")}
        </span>
      </RunLink>
    );
  }
  return (
    <SkyText size={200}>
      <span title={instantHoverTitle(succeededAt, zone)}>
        {formatInstant(succeededAt, zone, "seconds")}
      </span>
    </SkyText>
  );
}

function LastBuiltCell({ row }: { row: ModelRow }) {
  const zone = useDisplayTz();
  if (row.kind === "disabled") return <Dash label="Not built: disabled in dbt" />;
  const last = row.node.lastBuilt ?? null;
  const succeededAt = row.node.lastSucceededAt ?? null;
  if (!last && !succeededAt) {
    return <Dash label={isInlinedRow(row) ? INLINED_BY_DBT_SENTENCE : "Not built yet"} />;
  }
  const lastFailedAfter =
    last && isDbtFailedStatus(last.status) && (!succeededAt || last.at > succeededAt);
  return (
    <div className="flex flex-col" data-testid={`transformation-model-built-${row.name}`}>
      {lastSucceeded(last, succeededAt, zone)}
      {lastFailedAfter && last ? (
        <SkyText size={200} color="muted">
          {"Last attempt failed "}
          <RunLink runId={last.runId}>
            <span title={instantHoverTitle(last.at, zone)}>
              {formatInstant(last.at, zone, "seconds")}
            </span>
          </RunLink>
        </SkyText>
      ) : null}
    </div>
  );
}

function SelectedByCell({ row, context }: { row: ModelRow; context: CellContext }) {
  if (row.kind === "disabled") return <Dash label="Disabled in dbt: no dbt job selects it" />;
  const node = row.node;
  if (context.saved) return <SavedJobsCell node={node} name={row.name} context={context} />;
  if (!node.selectedByKnown && node.selectedBy.length === 0) {
    return (
      <SkyText size={200} color="muted">
        Not known
      </SkyText>
    );
  }
  if (node.selectedBy.length === 0) {
    // Review r1 S2: the column reads the last run's bundle. After a run of chosen models that
    // bundle is the choice alone, so a model left out says so rather than "No transformation"
    // (saved transformations may still select it; backlog: evaluate them against the manifest).
    if (context.chosenRun) {
      return (
        <SkyText size={200} color="muted" testId={`transformation-model-not-in-run-${row.name}`}>
          Not in the last run
        </SkyText>
      );
    }
    return (
      <SkyText size={200} color="muted">
        No dbt job
      </SkyText>
    );
  }
  return (
    <SkyText size={200} testId={`transformation-model-selected-by-${row.name}`}>
      {node.selectedBy.map((id) => context.selectionNames.get(id) ?? id).join(", ")}
      {node.selectedByKnown ? "" : " (partly known)"}
    </SkyText>
  );
}

/** After a run of chosen models: whether this model was one of them. */
function chosenRunLine(inRun: boolean, name: string): ReactElement {
  if (inRun) {
    return (
      <SkyText size={200} color="muted" testId={`transformation-model-in-run-${name}`}>
        Chosen in the last run
      </SkyText>
    );
  }
  return (
    <SkyText size={200} color="muted" testId={`transformation-model-not-in-run-${name}`}>
      Not in the last run
    </SkyText>
  );
}

/**
 * Round 7: the saved dbt jobs that select the model, whatever ran last; after a run of chosen
 * models, a second line says whether the run's choice took it, apart from the jobs.
 */
function SavedJobsCell({
  node,
  name,
  context,
}: {
  node: TransformationGraphNode;
  name: string;
  context: CellContext;
}) {
  const { ids, known } = shownJobs(node, true);
  const inRun = node.selectedBy.includes(CHOSEN_MODELS_ID);
  return (
    <div className="flex min-w-0 flex-col">
      {ids.length > 0 ? (
        <SkyText size={200} testId={`transformation-model-selected-by-${name}`}>
          {ids.map((id) => context.selectionNames.get(id) ?? id).join(", ")}
          {known ? "" : " (partly known)"}
        </SkyText>
      ) : (
        <SkyText size={200} color="muted">
          {known ? "No dbt job" : "Not known"}
        </SkyText>
      )}
      {context.chosenRun ? chosenRunLine(inRun, name) : null}
    </div>
  );
}

/** "View" on the customer's git host, at the run's commit when known, else the ref. */
export function SourceLink({
  graph,
  path,
  name,
  testId,
}: {
  graph: TransformationGraph;
  path: string | null | undefined;
  name: string;
  testId: string;
}) {
  const url = repoFileUrl(
    graph.bundle.repoUrl,
    graph.run?.commit ?? graph.bundle.ref,
    graph.bundle.subdir,
    path ?? null,
  );
  if (!url) {
    // #1306: the repository's address is Instance admin and above, so no link to its host.
    return graph.bundle.repoUrlWithheld ? (
      <Dash label="Source not available" hint={ASK_ADMIN_ABOUT_DBT_REPOSITORY} />
    ) : (
      <Dash label="Source not available" />
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
      aria-label={`View source of ${name} in ${repoHostName(graph.bundle.repoUrl) ?? "the repository"}, opens in a new tab`}
      data-testid={testId}
    >
      <SkyText size={200} color="accent">
        View
      </SkyText>
      <ExternalLinkIcon className="h-3 w-3" />
    </a>
  );
}

/* ------------------------------------------------------------------------------------- *
 * The view
 * ------------------------------------------------------------------------------------- */

const PAGE_SIZE = 25;

/**
 * Round 3 (owner, 2026-09-30, 3MpCjO.png): "Materialized" (144 px with its padding) had 9 percent
 * of the grid and ran into "Last result". The short columns have pixel widths, measured in
 * Chromium in both fonts (client/AGENTS.md), border-box: each holds its header, and Last built its
 * time on two lines at the comma ("Sep 29, 2026," 84 px). Model, Last result and Selected by share
 * what is left in proportion to a minimum that holds their header and value. The grouped grids
 * (1000 px) fit the accordion's pane at 1440 px.
 *
 * gridsfit (lead, 2026-09-30): a sorting grid (Group by None) added a flat 24 px to every column
 * for its 14 px sort icon and 8 px gap, a 1168 px floor that scrolled Source out of the 1131 px
 * pane at 1440 px. Its widths are now each column's own need with the icon, rounded up to 8 px
 * (header text + 32 padding + 22 icon; Last built also its date line), Model keeps 160 and Selected
 * by 168 (its need is 160; a share rounded to 0.01 percent must not fall under it): the floor,
 * 1120 px, fits.
 *
 * Round 6 (T3, W3): "Selected by" is "Transformation" (138 px of text, so 170 px, 192 sorting),
 * and a runs.start role gets the grid's 48 px selection column for "Run selected". Both fit only
 * because the Time column is gone: a build's time reads under its result ("took 1.35 s"), the
 * Runs list's pairing. Last result also gets more room for a failed model's cause (W3). Floors:
 * 992 grouped and 1080 sorting, 1040 and 1128 with the selection column; each fits its pane at
 * 1440 px (1099 in the accordion, 1131 flat).
 */
const MODEL_PIXEL_WIDTHS = {
  materialized: 152,
  lastBuiltAt: 128,
  rows: 88,
  source: 96,
} as const;
const MODEL_PIXEL_WIDTHS_SORTABLE: Record<keyof typeof MODEL_PIXEL_WIDTHS, number> = {
  materialized: 168,
  lastBuiltAt: 144,
  rows: 104,
  source: 120,
};
const MODEL_MIN_WIDTHS: Readonly<Record<string, number>> = {
  name: 176,
  lastResult: 176,
  selectedBy: 176,
};
const MODEL_MIN_WIDTHS_SORTABLE: Readonly<Record<string, number>> = {
  name: 168,
  lastResult: 176,
  selectedBy: 200,
};

/** Round 6 (T3): `dbt run` builds models, so only an enabled model can be chosen to run. */
function isChoosable(row: ModelRow): boolean {
  return row.kind === "node" && row.node.resourceType === "model";
}

interface GridRow {
  id: string;
  name: string;
  materialized: string;
  lastResult: string;
  lastBuiltAt: string;
  rows: number;
  /** Review nit M8: sort keys for "Transformation" (the names, as shown) and "Source" (the path). */
  selectedBy: string;
  source: string;
  row: ModelRow;
}

/**
 * The Rows value of a model whose kind reports no row count (`dbtNoRowsLabel`: a view, an
 * ephemeral model, a materialized view, a dynamic table); sorts below "not reported".
 */
const NO_ROWS = -2;

/** The Last result column's sortable text: disabled, the last build's word, or not built. */
function lastResultText(row: ModelRow, last: TransformationGraphNode["lastBuilt"]): string {
  if (row.kind === "disabled") return "Disabled in dbt";
  if (last) return lastResultWord(last.status).word;
  return isInlinedRow(row) ? "Inlined by dbt" : "Not built yet";
}

function gridRowOf(row: ModelRow, selectionNames: Map<string, string>, saved: boolean): GridRow {
  const last = row.kind === "node" ? (row.node.lastBuilt ?? null) : null;
  return {
    id: row.uniqueId,
    name: row.name,
    materialized: row.node.materialized ?? "",
    lastResult: lastResultText(row, last),
    lastBuiltAt: row.kind === "node" ? (row.node.lastSucceededAt ?? "") : "",
    // Walk 2026-10-02, D21, #1093: a view stores no rows; the 1 Snowflake reports is its DDL
    // statement. One rule with the server (`dbtNoRowsLabel`, `dbtNodeRows`).
    rows: dbtNoRowsLabel(row.node.materialized) !== null ? NO_ROWS : (last?.rowsAffected ?? -1),
    selectedBy:
      row.kind === "disabled"
        ? ""
        : shownJobs(row.node, saved)
            .ids.map((id) => selectionNames.get(id) ?? id)
            .join(", "),
    source: row.node.path ?? "",
    row,
  };
}

function ModelsGrid({
  group,
  context,
  sortable,
  testId,
  choice,
}: {
  group: ModelGroup;
  context: CellContext;
  sortable: boolean;
  testId: string;
  /**
   * Round 6 (T3): the view's chosen models (unique ids, across every group), for a runs.start
   * role; absent, the grid has no selection column.
   */
  choice?: { chosen: readonly string[]; onChange(ids: string[]): void };
}) {
  const data = useMemo(
    () => group.rows.map((row) => gridRowOf(row, context.selectionNames, context.saved)),
    [group.rows, context.selectionNames, context.saved],
  );
  const widthOf = (key: keyof typeof MODEL_PIXEL_WIDTHS): string =>
    `${(sortable ? MODEL_PIXEL_WIDTHS_SORTABLE : MODEL_PIXEL_WIDTHS)[key]}px`;
  const muted = (row: GridRow): string | undefined =>
    row.row.kind === "disabled" ? "text-muted-foreground" : undefined;
  /** The Rows cell: the count, a view (which stores none), or not reported. */
  const rowsCell = (row: GridRow): ReactElement => {
    if (row.rows >= 0) {
      return (
        <SkyText size={200} font="numeric" className={muted(row)}>
          {row.rows.toLocaleString()}
        </SkyText>
      );
    }
    const noRows = row.rows === NO_ROWS ? dbtNoRowsLabel(row.row.node.materialized) : null;
    if (noRows) return <Dash label={noRows} />;
    return (
      <Dash
        label="Row count not reported"
        hint={row.row.kind === "node" && row.row.node.lastBuilt ? ROWS_HINT : undefined}
      />
    );
  };
  const columns: SkyDataGridColumn<GridRow>[] = [
    {
      key: "name",
      header: "Model",
      width: "20%",
      sortable,
      renderCell: (_value, row) => <ModelNameCell row={row.row} context={context} />,
    },
    {
      key: "materialized",
      header: "Materialized",
      width: widthOf("materialized"),
      sortable,
      // "Table", not dbt's "table" (round 4), in the app's font. #1349: the hover reads the same
      // presenter word, not dbt's lowercase value repeated.
      renderCell: (_value, row) => (
        <SkyText
          size={200}
          color={row.row.kind === "disabled" ? "muted" : "default"}
          title={row.materialized ? materializationLabel(row.materialized) : undefined}
          testId={`transformation-model-materialized-${row.name}`}
        >
          {row.materialized ? materializationLabel(row.materialized) : "—"}
        </SkyText>
      ),
    },
    {
      key: "lastResult",
      header: "Last result",
      width: "17%",
      sortable,
      renderCell: (_value, row) => <LastResultCell row={row.row} />,
    },
    {
      key: "lastBuiltAt",
      header: "Last built",
      width: widthOf("lastBuiltAt"),
      sortable,
      renderCell: (_value, row) => <LastBuiltCell row={row.row} />,
    },
    {
      key: "rows",
      header: "Rows",
      width: widthOf("rows"),
      align: "right",
      sortable,
      renderCell: (_value, row) => rowsCell(row),
    },
    {
      // Round 6 (T3): the saved transformation that selects the model ("Selected by" before).
      key: "selectedBy",
      header: "dbt job",
      width: "15%",
      sortable,
      renderCell: (_value, row) => <SelectedByCell row={row.row} context={context} />,
    },
    {
      key: "source",
      header: "Source",
      width: widthOf("source"),
      sortable,
      renderCell: (_value, row) => (
        <SourceLink
          graph={context.graph}
          path={row.row.node.path}
          name={row.name}
          testId={`transformation-model-source-${row.name}`}
        />
      ),
    },
  ];
  const layout = pixelColumnLayout(columns, {
    minimums: sortable ? MODEL_MIN_WIDTHS_SORTABLE : MODEL_MIN_WIDTHS,
    selection: choice !== undefined,
  });
  const ids = useMemo(() => new Set(data.map((row) => row.id)), [data]);
  return (
    <section
      aria-label={`Models in ${group.label}`}
      data-testid={testId}
      style={{ "--transformation-models-floor": `${layout.floor}px` } as CSSProperties}
    >
      <SkyDataGrid<GridRow>
        data={data}
        columns={layout.columns}
        getRowId={(row) => row.id}
        className="[&_table]:min-w-(--transformation-models-floor)"
        testId={`${testId}-grid`}
        pagination={data.length > PAGE_SIZE}
        pageSize={PAGE_SIZE}
        emptyMessage="No model matches the filter"
        {...(choice
          ? {
              selectionMode: "multiple" as const,
              selectedRows: choice.chosen.filter((id) => ids.has(id)),
              isRowSelectable: (row: GridRow) => isChoosable(row.row),
              // Reconcile against this grid's rows only: a model chosen in another group, or
              // hidden by the filter, stays chosen (`TablePicker`'s rule).
              onSelectionChange: (selected: (string | number)[]) => {
                const shown = new Set(selected.map(String));
                choice.onChange([
                  ...choice.chosen.filter((id) => !ids.has(id)),
                  ...data
                    .filter((row) => shown.has(row.id) && isChoosable(row.row))
                    .map((row) => row.id),
                ]);
              },
            }
          : {})}
      />
    </section>
  );
}

function SourcesList({ group }: { group: ModelGroup }) {
  return (
    <ul className="flex flex-wrap gap-2" aria-label="Sources the project reads">
      {group.rows.map((row) => (
        <li key={row.uniqueId}>
          {/* The grid's cell size in the app's font (round 4): the 10 px monospace chips read
              as code, and "dbocustomers" ran together. */}
          <SkyBadge appearance="outline" color="subtle" size="medium">
            <SkyText size={200}>{row.name}</SkyText>
          </SkyBadge>
        </li>
      ))}
    </ul>
  );
}

function groupHeading(group: ModelGroup): string {
  if (group.sources) {
    return `${group.label} · ${group.rows.length} ${group.rows.length === 1 ? "source" : "sources"}`;
  }
  return [
    `${group.label} · ${group.models} ${group.models === 1 ? "model" : "models"}`,
    group.failed > 0 ? `${group.failed} failed` : null,
    group.disabled > 0 ? `${group.disabled} disabled in dbt` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** " · 12 models (2 disabled) · 30 tests · 4 sources": the header's counts after the project's run. */
function projectCounts(
  modelCount: number,
  disabledCount: number,
  testCount: number,
  sourceCount: number,
): string {
  const disabled = disabledCount > 0 ? ` (${disabledCount} disabled)` : "";
  return ` · ${modelCount} ${modelCount === 1 ? "model" : "models"}${disabled} · ${testCount} ${
    testCount === 1 ? "test" : "tests"
  } · ${sourceCount} ${sourceCount === 1 ? "source" : "sources"}`;
}

/** Round 6 (T3): Clear selection and Run selected (n), for a reader who may start a run. */
function RunSelectedControls({
  chosenCount,
  onClear,
  onRun,
}: {
  chosenCount: number;
  onClear: () => void;
  onRun: () => void;
}) {
  return (
    <span className="ml-auto flex items-center gap-2">
      {chosenCount > 0 ? (
        <SkyButton
          appearance="subtle"
          size="small"
          onClick={onClear}
          testId="transformation-models-clear-selection"
        >
          Clear selection
        </SkyButton>
      ) : null}
      <SkyButton
        appearance="secondary"
        size="small"
        icon={PlayIcon}
        disabled={chosenCount === 0}
        onClick={onRun}
        testId="transformation-models-run-selected"
      >
        {`Run selected (${chosenCount})`}
      </SkyButton>
    </span>
  );
}

/** The models by group: one sortable grid when ungrouped, else an accordion; or no match. */
function ModelGroups({
  groups,
  groupBy,
  failedOnly,
  context,
  choice,
  openItems,
  onToggle,
}: {
  groups: ModelGroup[];
  groupBy: ModelGroupBy;
  failedOnly: boolean;
  context: CellContext;
  choice: { chosen: readonly string[]; onChange(ids: string[]): void } | undefined;
  openItems: string[];
  onToggle: (items: string[]) => void;
}) {
  if (groups.length === 0) {
    return (
      <SkyText size={200} color="muted" testId="transformation-models-no-match">
        {failedOnly ? "No model failed in its last build." : "No model matches the filter."}
      </SkyText>
    );
  }
  const first = groups[0];
  if (groupBy === "none" && first && !first.sources) {
    return (
      <>
        <ModelsGrid
          group={first}
          context={context}
          sortable
          testId="transformation-models-group-all"
          choice={choice}
        />
        {groups
          .filter((group) => group.sources)
          .map((group) => (
            <details key={group.key} data-testid="transformation-models-sources">
              <summary>
                <SkyText size={200} weight="semibold">
                  {groupHeading(group)}
                </SkyText>
              </summary>
              <div className="pt-2">
                <SourcesList group={group} />
              </div>
            </details>
          ))}
      </>
    );
  }
  return (
    <SkyAccordion
      multiple
      openItems={openItems}
      onToggle={(_value, items) => onToggle(items)}
      testId="transformation-models-groups"
    >
      {groups.map((group) => (
        <SkyAccordionItem
          key={group.key}
          value={group.key}
          heading={
            <span
              data-testid={`transformation-models-heading-${group.sources ? "sources" : group.label}`}
            >
              {groupHeading(group)}
            </span>
          }
        >
          {group.key === SOURCES_GROUP_KEY ? (
            <SourcesList group={group} />
          ) : (
            <ModelsGrid
              group={group}
              context={context}
              sortable={false}
              testId={`transformation-models-group-${group.label}`}
              choice={choice}
            />
          )}
        </SkyAccordionItem>
      ))}
    </SkyAccordion>
  );
}

function NotListedNote({ count }: { count: number }) {
  return (
    <SkyText size={200} color="muted" testId="transformation-models-not-listed">
      {`${count} ${count === 1 ? "resource is" : "resources are"} not listed because ${
        count === 1 ? "its name uses" : "their names use"
      } characters this screen cannot show.`}
    </SkyText>
  );
}

/** `?group=`: a known grouping, else the default for these rows. */
function groupByOf(groupParam: string | null, rows: ModelRow[]): ModelGroupBy {
  return isModelGroupBy(groupParam) ? groupParam : defaultGroupBy(rows);
}

/**
 * The groups the reader opened for this grouping and filter, else the default open ones. The
 * choices are kept per grouping, and a filter has its own lane (what was folded), so clearing it
 * returns the groups to how the reader left them (#1015).
 */
function openItemsFor(
  openState: Record<string, string[]>,
  stateKey: string,
  defaultOpen: string[],
): string[] {
  return openState[stateKey] ?? defaultOpen;
}

export function TransformationModelsView({
  graph,
  graphState,
  onRetry,
  canStart,
  onRunNow,
  onRefresh,
  onNewDbtJob,
  onRunSelected,
}: TransformationModelsViewProps) {
  const [groupParam, setGroupParam] = useLandingParam("group");
  const [resultParam, setResultParam] = useLandingParam("result");
  const [filter, setFilter] = useState("");
  // Round 6 (T3): the chosen models by unique id, across every group and through a filter.
  const [chosen, setChosen] = useState<string[]>([]);

  const rows = useMemo(
    () => (graph ? modelRows(graph.nodes, graph.disabledNodes ?? []) : []),
    [graph],
  );
  const groupBy = groupByOf(groupParam, rows);
  const failedOnly = resultParam === "failed";
  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return rows.filter(
      (row) =>
        (!needle || row.name.toLowerCase().includes(needle)) && (!failedOnly || rowFailed(row)),
    );
  }, [rows, filter, failedOnly]);
  const groups = useMemo(
    () => groupModels(visible, groupBy, failedOnly || filter.trim() ? [] : (graph?.nodes ?? [])),
    [visible, groupBy, failedOnly, filter, graph],
  );

  // #1015: a name filter lists only groups that have a match, so each of them opens and the
  // matches show. Without one, a single group starts open and several start folded.
  const filtering = filter.trim() !== "";
  const defaultOpen = useMemo(() => {
    const built = groups.filter((group) => !group.sources);
    return filtering || built.length === 1 ? built.map((group) => group.key) : [];
  }, [groups, filtering]);
  const [openState, setOpenState] = useState<Record<string, string[]>>({});
  // While filtering, the reader's choice is what they FOLDED, never the open list, so a group
  // that starts matching as the text changes still opens. Clearing the filter forgets it.
  const [foldedState, setFoldedState] = useState<Record<string, string[]>>({});
  const stateKey = `${groupBy}|${failedOnly}`;
  const folded = foldedState[stateKey] ?? [];
  const openItems = filtering
    ? defaultOpen.filter((key) => !folded.includes(key))
    : openItemsFor(openState, stateKey, defaultOpen);

  const context: CellContext | null = useMemo(() => {
    if (!graph) return null;
    // Walk 2026-10-02, D4: a transformation the run left out, attributed by its own run, by name.
    const saved = Array.isArray(graph.savedSelection);
    const selectionNames = new Map([
      ...(graph.savedSelection ?? []).map((s) => [s.dbtJobId, s.name] as const),
      ...(graph.ownRuns ?? []).map((s) => [s.dbtJobId, s.name] as const),
      ...(graph.selection ?? []).map((s) => [s.dbtJobId, s.name] as const),
    ]);
    const order = (graph.selection ?? []).map((s) => s.dbtJobId);
    return {
      graph,
      selectionNames,
      // Round 7: the model's name opens a saved dbt job that selects it, never the run's choice.
      firstSelector: (node) =>
        saved
          ? (node.savedBy?.[0] ?? null)
          : (order.find((id) => node.selectedBy.includes(id)) ?? node.selectedBy[0] ?? null),
      chosenRun: order.includes(CHOSEN_MODELS_ID),
      saved,
    };
  }, [graph]);

  if (graphState === "loading" || (!graph && graphState !== "error")) {
    return <GraphLoading testId="transformation-models-loading" />;
  }
  if (graphState === "error" || !graph || !context) {
    return (
      <div data-testid="transformation-models-view">
        <GraphReadError onRetry={onRetry} testId="transformation-models-error" />
      </div>
    );
  }

  const empty = (
    <ProjectEmptyState
      graph={graph}
      noun="models"
      canStart={canStart}
      onRunNow={onRunNow}
      onRetry={onRetry}
      onNewDbtJob={onNewDbtJob}
      testId="transformation-models-empty"
    />
  );
  // One Models count on the screen (S11 PR review N1): the rows this view lists, the models
  // disabled in dbt included (they are listed, greyed), exactly as the view switch's "Models {n}"
  // and the drawer's "{n} models ({d} disabled)" count them.
  const modelCount = rows.length;
  const disabledCount = rows.filter((row) => row.kind === "disabled").length;
  const sourceCount = graph.nodes.filter((node) => node.resourceType === "source").length;
  // Review S3: the server's count of entries dropped for their name. `nodeCount` also counts
  // hooks, analyses and exposures, which are never listed, so a difference would blame them.
  const notListed = graph.project?.unlistedCount ?? 0;
  // Round 6 (T3): only rows of this graph count, so a refreshed list never runs a vanished model.
  const choosing = canStart && onRunSelected !== undefined;
  const chosenNames = rows
    .filter((row) => isChoosable(row) && chosen.includes(row.uniqueId))
    .map((row) => row.name)
    .sort((a, b) => a.localeCompare(b));
  const choosableCount = rows.filter((row) => isChoosable(row)).length;
  const choice = choosing ? { chosen, onChange: setChosen } : undefined;

  return (
    <div className="flex flex-col gap-3" data-testid="transformation-models-view">
      {graph.project === null || graph.run === null ? (
        empty
      ) : (
        <>
          <WalkedBackNote graph={graph} testId="transformation-models-walked-back" />
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <SkyText size={200} color="muted" testId="transformation-models-header">
              <ProjectFrom graph={graph} testId="transformation-models-from" />
              {projectCounts(modelCount, disabledCount, graph.tests.length, sourceCount)}
            </SkyText>
            <SkyButton
              className="ml-auto"
              appearance="subtle"
              size="small"
              icon={RetryIcon}
              onClick={onRefresh}
              testId="transformation-models-refresh"
            >
              Refresh
            </SkyButton>
          </div>

          <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
            <SkyRadioGroup
              label="Group by"
              layout="horizontal"
              name="transformation-models-group-by"
              value={groupBy}
              onChange={(value) => setGroupParam(value)}
              options={MODEL_GROUP_BYS.map((value) => ({ value, label: GROUP_BY_LABELS[value] }))}
              testId="transformation-models-group-by"
            />
            <SkyInput
              label="Filter models"
              value={filter}
              onChange={(event) => {
                const next = event.target.value;
                if (next.trim() === "") setFoldedState({});
                setFilter(next);
              }}
              placeholder="Name contains…"
              className="w-60"
              testId="transformation-models-filter"
            />
            {failedOnly ? (
              <SkyButton
                appearance="subtle"
                size="small"
                onClick={() => setResultParam(null)}
                testId="transformation-models-clear-filter"
              >
                Clear filter
              </SkyButton>
            ) : null}
            {choosing ? (
              <RunSelectedControls
                chosenCount={chosenNames.length}
                onClear={() => setChosen([])}
                onRun={() => onRunSelected?.(chosenNames, chosenNames.length === choosableCount)}
              />
            ) : null}
          </div>

          {failedOnly ? (
            <SkyText size={200} color="muted" testId="transformation-models-failed-only">
              Showing the models whose last build failed.
            </SkyText>
          ) : null}

          <ModelGroups
            groups={groups}
            groupBy={groupBy}
            failedOnly={failedOnly}
            context={context}
            choice={choice}
            openItems={openItems}
            onToggle={(items) => {
              if (filtering) {
                const shown = groups.filter((group) => !group.sources).map((group) => group.key);
                // Merge, never replace: a fold made under another filter text stays.
                setFoldedState((prev) => ({
                  ...prev,
                  [stateKey]: [
                    ...(prev[stateKey] ?? []).filter((key) => !shown.includes(key)),
                    ...shown.filter((key) => !items.includes(key)),
                  ],
                }));
              } else {
                setOpenState((prev) => ({ ...prev, [stateKey]: items }));
              }
            }}
          />

          {notListed > 0 ? <NotListedNote count={notListed} /> : null}
        </>
      )}
    </div>
  );
}
